// @flow
import { Map } from "immutable"
import fp from "lodash/fp"
import type { Dispatch } from "redux"
import type Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import cuckoo from "common/services/cuckoo/cuckoo"
import { bookedAtSelector } from "common/services/branding/selectors"
import { cuckooEvents } from "common/consts/enums"
import { currenciesSelector, currencySelector } from "common/services/currencies/selectors"
import convertPrice from "common/services/price/convertPrice"
import type { PassengerChanges } from "mmb/services/bookings/bookingsApi"
import type { BookingId, PassengerId, PriceTotal } from "mmb/types/primitiveTypes"
import * as bookingActions from "../../../services/index"
import { getFileURIs } from "mmb/scenes/Booking/components/BookingFileUpload/BookingFileUploadHelpers"
import { submitForPayment } from "../../BookingPayment/services/paymentActions"
import { bidSelector, currentBookingSpecificPassengerSelector } from "../../../services/selector"
import { mapperCreatedBookingChange } from "../../../services/mapper/services/mapperManageBookingChange"
import { compareUsers, formatDate, formatUser } from "./helpers"
import { DATE_FORMAT, EDITING_STEPS } from "../components/constants"
import type { SubmitForPaymentActionPayload } from "../../BookingPayment/records/PropTypes"
import {
  deletePassengerEditRequestDetail,
  getPassengerEditRequestDetail,
  orderNameChange,
  submitPassengerEditFrom,
} from "../../../../../../../services/bookings/bookingsApi"
import type { ActivePaxRequestsIds } from "./PassengerChangesTypes"
import { BOOKING_STATUSES } from "../../../../../../../consts/booking"
import type {
  ActionWithoutPayload,
  ActionWithPayload,
  EditingStepProps,
  PassengerEdit,
  State,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerEdit/services/types"
import {
  CLEAR_USER_EDITING,
  DELETE_CORRECTION,
  PASSENGER_EDIT,
  PAX_EDIT_STATUS,
  SET_NEW_PASSENGER_DATA,
  SET_PASSENGER_EDIT_STEP,
  SHOW_PAX_EDIT_OFFER,
  SHOW_PAX_EDIT_OFFER_FROM_LOADING,
  SUBMIT_EDIT_USER_FORM,
  SUBMIT_ORDER_CORRECTION,
  TOGGLE_LEAVE_CONFIRMATION_PASSENGER_EDIT,
  TOGGLE_LOADING,
  TOGGLE_PASSENGER_EDIT,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerEdit/services/consts"
import { ACTION } from "mmb/scenes/AccountBooking/components/Booking/services/consts"

const initialState: State = Map({
  loading: false,
  user: {},
  newUserData: {},
  step: EDITING_STEPS.SELECTION,
  leaveConfirmationOpen: false,
  changePrice: 0,
  changeId: null,
})

export default function passengerEditReducer(
  state: State = initialState,
  action: ActionWithPayload | ActionWithoutPayload,
) {
  switch (action.type) {
    case TOGGLE_PASSENGER_EDIT:
      return state.set("user", action.payload).set("step", EDITING_STEPS.FORM)
    case SHOW_PAX_EDIT_OFFER:
      return state
        .set("user", action.payload.prev)
        .set("newUserData", action.payload.after)
        .set("changeId", action.payload.id)
        .set("changePrice", action.payload.price)
        .set("step", EDITING_STEPS.SUMMARY_OFFER)
    case SHOW_PAX_EDIT_OFFER_FROM_LOADING:
      return state
        .set("changeId", action.payload.id)
        .set("changePrice", action.payload.price)
        .set("step", EDITING_STEPS.SUMMARY)
        .set("loading", false)
    case SET_PASSENGER_EDIT_STEP:
      return state.set("step", action.payload)
    case SET_NEW_PASSENGER_DATA:
      return state.set("newUserData", action.payload)
    case TOGGLE_LOADING:
      return state.set("loading", action.payload)
    case TOGGLE_LEAVE_CONFIRMATION_PASSENGER_EDIT:
      return state.set("leaveConfirmationOpen", !state.get("leaveConfirmationOpen"))
    case CLEAR_USER_EDITING:
      return state.merge(state, initialState)
    default:
      return state
  }
}

export const currentlyEditingUserSelector = (state: State) => state.passengerEdit.get("user")
export const editingStepSelector = (state: State) => state.passengerEdit.get("step")
export const editPriceSelector = (state: State) => state.passengerEdit.get("changePrice")
export const changeIdSelector = (state: State) => state.passengerEdit.get("changeId")
export const editedUserDataSelector = (state: State) => state.passengerEdit.get("newUserData")
export const paxIdsWithActiveRequestSelector = (state: State): ActivePaxRequestsIds => {
  return state.booking
    .get("data")
    .bookingChanges.filter(c => c.stage !== PAX_EDIT_STATUS.COMPLETED)
    .map(c => c.passenger_ids[0])
}

export const nameCorrectionEnabledSelector = (state: State): boolean => {
  const booking = state.booking.get("data")
  return (
    state.splitster.get("PASSENGER_CHANGE").value === "enabled" &&
    booking.status !== BOOKING_STATUSES.REFUNDED &&
    booking.status !== BOOKING_STATUSES.REFUNDING &&
    booking.journey.endsInFuture()
  )
}

export const setPassengerToState = (payload: Passenger) => ({
  type: TOGGLE_PASSENGER_EDIT,
  payload,
})

export const destroyPassengerEditForm = () => ({
  type: "redux-form/DESTROY",
  form: "editPassenger",
})

const toggleLoading = (payload: boolean) => ({
  type: TOGGLE_LOADING,
  payload,
})

export const setNewUserData = (data: PassengerEdit) => ({
  type: SET_NEW_PASSENGER_DATA,
  payload: {
    ...data,
    birth: formatDate(data.birth),
    document: {
      code: data.document.code,
      expiration: formatDate(data.document.expiration),
    },
  },
})

export const showPassengerEditOffer = (
  after: PassengerEdit,
  prev: Passenger,
  price: PriceTotal,
  id: number,
) => ({
  type: SHOW_PAX_EDIT_OFFER,
  payload: { after, prev, price, id },
})

export const showOfferFromLoading = (price: PriceTotal, id: number) => ({
  type: SHOW_PAX_EDIT_OFFER_FROM_LOADING,
  payload: { price, id },
})

export const setStep = (payload: EditingStepProps) => ({
  type: SET_PASSENGER_EDIT_STEP,
  payload,
})

export const clearUserEditing = () => ({
  type: CLEAR_USER_EDITING,
})

// Transform data for redux form
export const setPassenger = (paxId: PassengerId) => (
  dispatch: Dispatch<any>,
  getState: () => State,
) => {
  let pax = { ...currentBookingSpecificPassengerSelector(getState(), paxId) }
  pax.birth = pax.birth.format(DATE_FORMAT)

  pax = fp.set(
    "document.expiration",
    pax.document.code && !pax.document.expiration ? "no-expiration" : pax.document.expiration,
    pax,
  )

  dispatch(setPassengerToState(pax))
  cuckoo.track(cuckooEvents.PAX_CHANGE_FORM_OPENED, { passengerId: paxId })
}

export const submitPassengerEditForm = (data: PassengerEdit) => async (
  dispatch: Dispatch<any>,
  getState: () => State,
) => {
  dispatch(setNewUserData(data))
  dispatch(toggleLoading(true))

  const state = getState()
  const formData = editedUserDataSelector(state)
  const user = currentlyEditingUserSelector(state)

  const newUserData = formatUser(formData)
  const oldUserData = formatUser(user)
  const compared = compareUsers(newUserData, oldUserData)

  const fileURIs = await getFileURIs(formData.files)
  const payload: PassengerChanges = {
    passenger_changes: {
      [user.id]: { ...compared, files: fileURIs },
    },
  }
  cuckoo.track(cuckooEvents.PAX_CHANGE_FORM_SUBMIT, { passengerId: user.id, changes: compared })

  const bid = bidSelector(state)
  const promise = submitPassengerEditFrom(bid, payload)
  const changedFields = Object.keys(compared)

  const ok = await dispatch({
    type: ACTION,
    action: SUBMIT_EDIT_USER_FORM,
    promise,
    _track: { data: changedFields },
    bid,
    payload,
  })

  if (ok) {
    return promise.then(res => {
      function callRequestDetail(retryCount) {
        // eslint-disable-next-line camelcase
        getPassengerEditRequestDetail(bid, res.id).then(({ id, price_amount, stage }) => {
          if (stage === PAX_EDIT_STATUS.DETERMINED) {
            cuckoo.track(cuckooEvents.PAX_CHANGE_OFFER, {
              type: "Auto",
              offeredPrice: price_amount,
            })
            dispatch(showOfferFromLoading(price_amount, id))
          } else if (retryCount === 3 || stage === PAX_EDIT_STATUS.BACKOFFICE) {
            dispatch(toggleLoading(false))
            dispatch(setStep(EDITING_STEPS.THANK_YOU))
            cuckoo.track(cuckooEvents.PAX_CHANGE_OFFER, { type: "Manual" })
          } else {
            setTimeout(() => {
              callRequestDetail(retryCount + 1)
            }, 5000)
          }
        })
      }
      callRequestDetail(0)
    })
  }
  dispatch(toggleLoading(false))
  dispatch(setStep(EDITING_STEPS.ERROR))

  return ok
}

export const toggleConfirmationModal = () => ({
  type: TOGGLE_LEAVE_CONFIRMATION_PASSENGER_EDIT,
})

export const closePassengerEditModal = () => (dispatch: Dispatch<any>, getState: () => State) => {
  const state = getState()
  const editingStep = editingStepSelector(state)

  switch (editingStep) {
    case EDITING_STEPS.SUMMARY:
      return dispatch(toggleConfirmationModal())
    case EDITING_STEPS.THANK_YOU:
    case EDITING_STEPS.ERROR:
      dispatch(bookingActions.reloadBooking(bidSelector(state)))
      break
    default:
  }
  return dispatch(bookingActions.closeModal())
}

// Cancel whole request from confirmation modal (Phase 02)
export const cancelEditRequest = () => (dispatch: Dispatch<any>) => {
  dispatch(bookingActions.closeModal())
  dispatch(clearUserEditing)
}

export const deletePassengerEditRequestById = (bid: BookingId, requestId: number) => async (
  dispatch: Dispatch<any>,
) => {
  const promise = deletePassengerEditRequestDetail(bid, requestId)

  const ok = await dispatch({
    type: DELETE_CORRECTION,
    action: DELETE_CORRECTION,
    promise,
  })

  if (ok) {
    return promise.catch(err => err).then(dispatch(bookingActions.closeModal()))
  }
  return ok
}

export const orderNameCorrection = (serviceForPayment: SubmitForPaymentActionPayload) => async (
  dispatch: Dispatch<any>,
  getState: () => State,
) => {
  const state = getState()
  const { payload } = serviceForPayment

  const promise = orderNameChange(bidSelector(state), {
    ...payload,
    currency: currencySelector(state),
    booked_at: bookedAtSelector(state),
  })

  const ok = await dispatch({
    type: SUBMIT_ORDER_CORRECTION,
    action: SUBMIT_ORDER_CORRECTION,
    payload,
    promise,
    _track: { payload },
  })

  if (ok) {
    return promise
      .then(res => res.detail)
      .then(mapperCreatedBookingChange)
      .then(payload.price === 0 ? dispatch(setStep(EDITING_STEPS.THANK_YOU)) : null)
      .catch(err => err)
  }
  return ok
}

export const toPayment = () => (dispatch: Dispatch<any>, getState: () => State) => {
  const state = getState()
  const price = editPriceSelector(state)
  const currency = currencySelector(state)
  const currencies = currenciesSelector(state)

  dispatch(
    submitForPayment({
      category: PASSENGER_EDIT,
      bid: bidSelector(state),
      price: convertPrice(price, currency, currencies),
      currency,
      orderAction: orderNameCorrection,
      payload: {
        pax: editedUserDataSelector(state),
        price,
        requestId: changeIdSelector(state),
      },
    }),
  )
}
